import { useMemo } from 'react'
import useSWR from 'swr'
import { z } from 'zod'

import type { Course } from 'types/data'
import { useAltCategoriesFetcher } from 'experiments/pen-2866_v1_alternative_categories/swrFetcher'

import { useCategories } from 'components/Navigation/consumer/dataFetching'

export const useCoursesByCategory = (slug: string) => {
  const fetcher = useAltCategoriesFetcher()
  const coursesUrl = getCoursesByCategoryURL(slug)

  const {
    data: dataCourse,
    isValidating: isValidatingCourse,
    error: errorCourse,
  } = useSWR<Course[]>(coursesUrl, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  const { dataCamp, isValidatingCamp, errorCamp } = useCampsByCategory(slug)

  const coursesByCategory = useMemo(() => {
    const courses: ReturnType<typeof reformatCourses> | undefined = dataCourse
      ? reformatCourses(dataCourse)
      : undefined

    const camps: ReturnType<typeof reformatCamps> | undefined = dataCamp
      ? reformatCamps(dataCamp)
      : undefined

    if (!courses || !camps) return null

    const parsedCourses = parseContent(courses)
    const parsedCamps = parseContent(camps)

    return [...parsedCourses, ...parsedCamps]
  }, [dataCourse, dataCamp])

  return {
    coursesByCategory,
    isValidating: isValidatingCourse || isValidatingCamp,
    error: errorCourse || errorCamp,
  }
}

// aux functions
const useCampsByCategory = (slug: string) => {
  const fetcher = useAltCategoriesFetcher()
  const { categories } = useCategories()

  const category = categories.filter((c) => c.slug === slug)?.[0]
  const categoryID = category ? Number(category.id) : null

  const {
    data: dataAllCamps,
    isValidating: isValidatingCamp,
    error: errorCamp,
  } = useSWR<TCamp[]>('/jsonapi/v1/camps?deep=true', fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  const dataCamp = dataAllCamps?.filter((c) => {
    if (!categoryID) return false
    if (c.primaryCategory?.id === categoryID) return true
    return (c.categories || []).map((cat) => cat.id).includes(categoryID)
  })

  return {
    dataCamp,
    isValidatingCamp,
    errorCamp,
  }
}

const getCoursesByCategoryURL = (slug: string) =>
  `/jsonapi/v1/courses?filter[category]=${slug}`

export const parseContent = (
  content:
    | ReturnType<typeof reformatCourses>
    | ReturnType<typeof reformatCamps>,
) =>
  content
    .map((c) => {
      const result = ZodContent.safeParse(c)
      if (result.success) return result.data
      else return null
    })
    .filter(removeNulls)

const ZodContent = z.object({
  brightcoveVideoId: z.string().nullish(),
  mediaUuid: z.string().optional(),
  instructorBio: z.string().nullish(),
  duration: z.number(),
  id: z.coerce.string(),
  slug: z.string(),
  title: z.string(),
  headline: z.string(),
  instructorName: z.string(),
  instructorImage: z.string(),
  contentCount: z.number(),
  skill: z.string(), // "Screenwriting"
  type: z.union([
    z.literal('camp'),
    // see CourseType.ts
    z.literal('course'),
    z.literal('series'),
  ]),
  uuid: z.string(),
  totalSeconds: z.number().nullish(),
  nameplate: z.string(),
  primary2x3: z.string(),
  tag: z.string().nullish(),
})

const removeNulls = <T>(value: T | null): value is T => value !== null

const reformatCamps = (camps: TCamp[]) =>
  camps.map((camp) => {
    let campDurationSecs = 0
    camp.campModules?.forEach((campModule) => {
      campModule.campTasks?.forEach((campTask) => {
        campDurationSecs += campTask.durationSecs
      })
    })

    return {
      contentCount: camp.campModules.length,
      duration: campDurationSecs,
      brightcoveVideoId: camp.trailer?.brightcoveVideoId,
      mediaUuid: camp.trailer?.mediaUuid,
      id: camp.id,
      instructorImage: camp.primary1x1,
      instructorBio: camp.instructorBio.description,
      slug: camp.slug,
      type: 'camp',
      headline: camp.title,
      instructorName: camp.headline,
      skill: camp.supportingHeadline,
      title: camp.title,
      uuid: camp.uuid,
      primaryCategory: camp.primaryCategory,
      categories: camp.categories,
      nameplate: camp.nameplateImage,
      primary2x3: camp.primary2x3,
    }
  })

export const reformatCourses = (courses: Course[]) =>
  courses.map(
    ({ instructorBio, numChapters, shortOverview, image4x3, ...course }) => ({
      brightcoveVideoId: course.brightcoveVideoId,
      mediaUuid: course.mediaUuid || course.sample?.mediaUuid,
      duration: course.totalSeconds,
      id: course.id,
      instructorBio: instructorBio || shortOverview,
      instructorName: course.instructorName,
      isMultiInstructor: course.isMultiInstructor,
      multiInstructorName: course.multiInstructorName,
      contentCount: numChapters,
      instructorImage: image4x3,
      skill: course.skill,
      slug: course.slug,
      title: course.title,
      headline: course.headline,
      type: course.type,
      totalSeconds: course.totalSeconds,
      uuid: course.uuid,
      primaryCategory: course.primaryCategory,
      categories: course.categories,
      nameplate: course.nameplate,
      primary2x3: course.primary2x3,
      tag: course.tag,
    }),
  )

// types
export type TCourse = z.infer<typeof ZodContent>

type TCamp = {
  campModules: Array<{
    campTasks: Array<{
      durationSecs: number
    }>
  }>
  id: string
  instructorBio: { description: string }
  slug: string
  title: string
  primary1x1: string
  primary2x3: string
  headline: string
  primaryCategory: { id: number } | null | undefined
  categories: Array<{ id: number }> | null | undefined
  supportingHeadline: string
  trailer: { brightcoveVideoId?: string; mediaUuid: string }
  totalDurationDays: number
  uuid: string
  nameplateImage: string
}
